var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { busService } from "../../bus";
import { AskErrorMessage, isPromise, hashCode, RemoveSubModelMessage, LayoutUpdatedMessage, UTILITY_ROOM_UID } from "flavr-common";
import { DynamicHTMLScriptEventMessage, ResponseDynamicHTMLScriptEventMessage, ResponseScriptEventToDynamicHTMLMessage, ScriptEventToDynamicHTMLMessage } from "../../../messages/scripting/ScriptMessages";
import { scriptService } from "./ScriptService";
import { modelPool } from "@modules/model";
const UIBusSupportedParams = ["number", "string", "boolean", "undefined"];
class UIBusService {
    constructor() {
        this.connection = busService.connection("UIBusService");
        this.rooms = new Map();
        this.modelToRoomMap = new Map();
        this.handleDynamicHTMLScriptEventMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            let roomUid = msg.roomUid;
            if (msg.roomUid === UTILITY_ROOM_UID) {
                if (scriptService.context && scriptService.context.currentRoom) {
                    roomUid = scriptService.context.currentRoom.roomUID;
                }
            }
            const room = this.rooms.get(roomUid);
            if (!room) {
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskErrorMessage(""));
                }
                return;
            }
            let isHandled = false;
            try {
                const pattern = room.hashPatternMap.get(msg.patternHash);
                if (pattern === undefined) {
                    // throw new Error("No subscription for pattern");
                    return;
                }
                const subscription = room.subscriptions.get(pattern);
                if (!subscription) {
                    // throw new Error("No subscription for pattern");
                    return;
                }
                if (!modelPool.hasModel(msg.uid)) {
                    throw new Error("Model uid is unknown");
                }
                yield this.processSubscription(room, pattern, askId, ...msg.params);
                isHandled = true;
            }
            catch (error) {
                console.error(error);
            }
            if (askId !== undefined && isHandled)
                return;
            let lastPatternData = room.lastPatternData.get(msg.patternHash);
            if (!lastPatternData) {
                lastPatternData = {
                    askRequests: new Map()
                };
                room.lastPatternData.set(msg.patternHash, lastPatternData);
            }
            if (askId !== undefined) {
                lastPatternData.askRequests.set(askId, msg.params);
            }
            else {
                lastPatternData.params = msg.params;
            }
        });
        this.handleRemoveSubModelMessage = (msg) => {
            this.removeByModelUid(msg.uid);
        };
        /**
         * Clears lastPatternData map
         * @doesNotThrow
         */
        this.handleLayoutUpdatedMessage = () => {
            for (const room of this.rooms.values()) {
                room.lastPatternData.clear();
            }
        };
    }
    init() {
        this.connection.subscribeByTypeOrAskRequest(DynamicHTMLScriptEventMessage, this.handleDynamicHTMLScriptEventMessage);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByType(LayoutUpdatedMessage, this.handleLayoutUpdatedMessage);
    }
    subscribe(roomUid, uid, pattern, callback) {
        let room = this.rooms.get(roomUid);
        if (!room) {
            room = {
                subscriptions: new Map(),
                hashPatternMap: new Map(),
                patternHashMap: new Map(),
                uidPatternMap: new Map(),
                lastPatternData: new Map()
            };
            this.rooms.set(roomUid, room);
        }
        let subscription = room.subscriptions.get(pattern);
        if (!subscription) {
            const patternHash = hashCode(pattern);
            subscription = {
                hash: patternHash,
                models: new Map()
            };
            room.subscriptions.set(pattern, subscription);
            room.hashPatternMap.set(patternHash, pattern);
        }
        let callbacks = subscription.models.get(uid);
        if (!callbacks) {
            callbacks = new Set();
            subscription.models.set(uid, callbacks);
        }
        callbacks.add(callback);
        let patternSet = room.uidPatternMap.get(uid);
        if (!patternSet) {
            patternSet = new Set();
            room.uidPatternMap.set(uid, patternSet);
        }
        patternSet.add(pattern);
        const lastData = room.lastPatternData.get(subscription.hash);
        if (lastData) {
            if (lastData.askRequests.size > 0) {
                for (const [askId, params] of lastData.askRequests) {
                    this.processSubscription(room, pattern, askId, ...params).catch((error) => {
                        console.error(error);
                    });
                }
                lastData.askRequests.clear();
            }
            else if (lastData.params) {
                this.processSubscription(room, pattern, undefined, ...lastData.params).catch((error) => {
                    console.error(error);
                });
            }
        }
    }
    unsubscribe(roomUid, uid, pattern, callback) {
        const room = this.rooms.get(roomUid);
        if (!room)
            return;
        const subscription = room.subscriptions.get(pattern);
        if (!subscription)
            return;
        const callbacks = subscription.models.get(uid);
        if (!callbacks)
            return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            subscription.models.delete(uid);
            this.modelToRoomMap.delete(uid);
            const patternSet = room.uidPatternMap.get(uid);
            if (patternSet) {
                patternSet.delete(pattern);
                if (patternSet.size === 0) {
                    room.uidPatternMap.delete(uid);
                }
            }
        }
        if (subscription.models.size === 0) {
            room.subscriptions.delete(pattern);
            const patternHash = room.patternHashMap.get(pattern);
            room.patternHashMap.delete(pattern);
            if (patternHash !== undefined) {
                room.hashPatternMap.delete(patternHash);
            }
            if (room.subscriptions.size === 0) {
                this.rooms.delete(roomUid);
            }
        }
    }
    removeByModelUid(uid) {
        const roomUid = this.modelToRoomMap.get(uid);
        if (roomUid === undefined)
            return;
        this.modelToRoomMap.delete(uid);
        const room = this.rooms.get(roomUid);
        if (!room)
            return;
        const patternSet = room.uidPatternMap.get(uid);
        if (!patternSet)
            return;
        for (const pattern of patternSet) {
            const subscription = room.subscriptions.get(pattern);
            if (!subscription)
                continue;
            subscription.models.delete(uid);
        }
        if (room.subscriptions.size === 0) {
            this.rooms.delete(roomUid);
        }
    }
    send(roomUid, pattern, ...params) {
        const room = this.rooms.get(roomUid);
        if (!room)
            throw new Error("Can't find room");
        if (typeof pattern !== "string") {
            throw new TypeError("sendMessage: funcName must be of type string");
        }
        for (const param of params) {
            const type = typeof param;
            if (!UIBusSupportedParams.includes(type)) {
                throw new TypeError(`sendMessage: parameter ${String(param)} with type ${type} isn't supported`);
            }
        }
        let patternHash = room.patternHashMap.get(pattern);
        if (patternHash === undefined) {
            patternHash = hashCode(pattern);
            room.patternHashMap.set(pattern, patternHash);
        }
        this.connection.send(new ScriptEventToDynamicHTMLMessage(roomUid, patternHash, params));
    }
    ask(roomUid, pattern, ...params) {
        return __awaiter(this, void 0, void 0, function* () {
            const room = this.rooms.get(roomUid);
            if (!room)
                throw new Error("");
            if (typeof pattern !== "string") {
                throw new TypeError("sendMessage: funcName must be of type string");
            }
            for (const param of params) {
                const type = typeof param;
                if (!UIBusSupportedParams.includes(type)) {
                    throw new TypeError(`sendMessage: parameter ${String(param)} with type ${type} isn't supported`);
                }
            }
            let patternHash = room.patternHashMap.get(pattern);
            if (patternHash === undefined) {
                patternHash = hashCode(pattern);
                room.patternHashMap.set(pattern, patternHash);
            }
            const response = yield this.connection.askByMessageType(new ScriptEventToDynamicHTMLMessage(roomUid, patternHash, params), ResponseScriptEventToDynamicHTMLMessage);
            return response.value;
        });
    }
    onRoomInit(_roomUid) { }
    validReturnment(returnment) {
        const type = typeof returnment;
        if (!UIBusSupportedParams.includes(type)) {
            throw new Error(`validReturnment: type ${type} for returnment ist not supported`);
        }
        return returnment;
    }
    processSubscription(roomData, pattern, askId, ...params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const subscription = roomData.subscriptions.get(pattern);
                if (!subscription) {
                    return;
                }
                const modelResults = [];
                for (const [uid, callbacks] of subscription.models) {
                    const results = [];
                    modelResults.push({
                        uid,
                        results
                    });
                    for (const subscriptionCallback of callbacks) {
                        try {
                            const funcResult = subscriptionCallback(...params);
                            if (isPromise(funcResult)) {
                                const asyncResult = yield Promise.resolve(funcResult);
                                this.validReturnment(asyncResult);
                                results.push(asyncResult);
                                continue;
                            }
                            this.validReturnment(funcResult);
                            results.push(funcResult);
                        }
                        catch (error) {
                            console.error("process callback", error);
                        }
                    }
                }
                if (askId !== undefined) {
                    let sendResult;
                    // only the first return is currently supported
                    if (modelResults.length > 0) {
                        const data = modelResults[0];
                        sendResult = data.results[0];
                    }
                    this.connection.answer(askId, new ResponseDynamicHTMLScriptEventMessage(sendResult));
                }
            }
            catch (error) {
                console.error("handleDynamicHTMLScriptEventMessage", error);
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskErrorMessage(JSON.stringify(error)));
                }
            }
        });
    }
}
export const uiBusService = new UIBusService();
